import { createContext, useContext, useState, useEffect } from 'react'
import type { ReactNode } from 'react'
import { api } from './api'

export interface AuthUser {
  id: string
  username: string
  display_name: string
  role: string
}

interface AuthContextType {
  user: AuthUser | null
  loading: boolean
  needsSetup: boolean
  login: (username: string, password: string) => Promise<void>
  setup: (username: string, password: string, displayName: string) => Promise<void>
  logout: () => Promise<void>
  refresh: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null)
  const [loading, setLoading] = useState(true)
  const [needsSetup, setNeedsSetup] = useState(false)

  async function checkAuth() {
    try {
      const me = await api.authMe()
      setUser(me)
      setNeedsSetup(false)
    } catch {
      setUser(null)
      // Check if setup is needed
      try {
        const status = await api.authSetupStatus()
        setNeedsSetup(status.needsSetup)
      } catch {
        setNeedsSetup(false)
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => { checkAuth() }, [])

  async function login(username: string, password: string) {
    const result = await api.authLogin(username, password)
    setUser(result.user)
    setNeedsSetup(false)
  }

  async function setup(username: string, password: string, displayName: string) {
    const result = await api.authSetup(username, password, displayName)
    setUser(result.user)
    setNeedsSetup(false)
  }

  async function logout() {
    await api.authLogout()
    setUser(null)
  }

  async function refresh() {
    await checkAuth()
  }

  return (
    <AuthContext.Provider value={{ user, loading, needsSetup, login, setup, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth() {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within AuthProvider')
  return ctx
}
